import React from "react";
import { compose, withStateHandlers } from "recompose";
import { chain, map } from "lodash";
import { Page } from "components/common";
import { withDocumentRenderer } from "components";
import { Trans, useTranslation } from "react-i18next";
import { StatementDocument } from "components/documents";
import moment from "moment";
import { useForm, FormProvider } from "react-hook-form";
import { DatePicker, FormGroup } from "../../components/react-hook-form";
import { useQuery } from "react-query";
import { useParams } from "react-router";
import { useXeroClient } from "../../hooks";
import { formatAccountCurrency } from "../../services/util";
import { DocumentLink } from "../../components";
import { Table } from "reactstrap";
import BackButton from "components/refactoring/BackButton";

const defaultAsAt = moment().format("YYYY-MM-DD");

export const Statement = ({
  DocumentRenderer,
  DownloadDocument,
  EmailDocument,
  setState,
}) => {
  const { t } = useTranslation();
  const { id } = useParams();

  const methods = useForm({
    defaultValues: {
      asAt: defaultAsAt,
    },
  });
  const client = useXeroClient();
  const { watch } = methods;
  const asAt = watch("asAt");

  const query = useQuery(
    ["statement", id, asAt],
    async () => {
      const data = await Promise.props({
        contact: client.get(`contacts/${id}`).get("data"),
        invoices: client
          .get("invoices", {
            params: {
              extra_params: {
                ContactIDs: id,
                Date__lte: asAt,
                Statuses: "AUTHORISED",
              },
            },
          })
          .get("data"),
        creditNotes: client
          .get("creditnotes", {
            params: {
              extra_params: {
                ContactIDs: id,
                Date__lte: asAt,
                Statuses: "AUTHORISED",
              },
              raw: `Status="AUTHORISED" AND Contact.ContactID=guid("${id}")`,
            },
          })
          .get("data"),
      });

      const { invoices, creditNotes } = data;

      const grouped = chain([...invoices, ...creditNotes])
        .orderBy("Date")
        .groupBy("CurrencyCode")
        .map((v, currency) => {
          let balanceDue = 0;

          const data = map(v, (i) => {
            const { Type } = i;
            const isReceivableInvoice = Type === "ACCREC";
            const isPayableInvoice = Type === "ACCPAY";
            const isReceivableCreditNote = Type === "ACCRECCREDIT";
            const isPayableCreditNote = Type === "ACCPAYCREDIT";

            let AmountDue = Number(i.AmountDue);
            let Total = Number(i.Total);
            let AmountPaid = Number(i.AmountPaid);
            let RemainingCredit = Number(i.RemainingCredit);
            let CreditUsed = Total - RemainingCredit;
            let Payments = 0;
            let Remaining = 0;

            if (isReceivableInvoice) {
              Payments = AmountPaid;
              Remaining = AmountDue;
            } else if (isReceivableCreditNote) {
              Payments = CreditUsed * -1;
              Remaining = RemainingCredit * -1;
              Total *= -1;
            } else if (isPayableInvoice) {
              Payments = AmountPaid * -1;
              Remaining = AmountDue * -1;
              Total *= -1;
            } else if (isPayableCreditNote) {
              Payments = CreditUsed;
              Remaining = RemainingCredit;
            }

            balanceDue += Remaining;

            return {
              ...i,
              isInvoice: isReceivableInvoice || isPayableInvoice,
              isCreditNote: isPayableCreditNote || isReceivableCreditNote,
              isReceivableCreditNote,
              isReceivableInvoice,
              isPayableCreditNote,
              isPayableInvoice,
              Total,
              Payments,
              Remaining,
            };
          });

          return {
            currency,
            data,
            balanceDue,
          };
        })
        .value();

      return {
        ...data,
        grouped,
        date: asAt,
      };
    },
    {
      keepPreviousData: true,
      onSuccess: (v) => setState(v),
    }
  );

  const { data, isLoading } = query;

  if (isLoading) return null;

  const { contact, grouped } = data;

  return (
    <Page
      title={t("Statement for {{ contact.Name }}", { contact })}
      translate={false}
    >
      <BackButton/>
      <FormProvider {...methods}>
        <FormGroup
          label={t("As at")}
          name="asAt"
          required
          htmlFor="asAt"
          className="tw-max-w-sm pb-2"
        >
          <DatePicker className="form-control" name="asAt" id="asAt" />
        </FormGroup>
      </FormProvider>
      {grouped.length > 0 ? 
      <div>
      {grouped.map((i) => (
        <div key={i.currency}>
          <h5>
            <Trans>Balance due in {{ currency: i.currency }}</Trans>
          </h5>
          <Table className="table table-sm small" responsive>
            <thead>
              <tr>
                <th className="text-nowrap">
                  <Trans>Date</Trans>
                </th>
                <th className="text-nowrap">
                  <Trans>Activity</Trans>
                </th>
                <th className="text-nowrap">
                  <Trans>Reference</Trans>
                </th>
                <th className="text-nowrap">
                  <Trans>Due Date</Trans>
                </th>
                <th className="text-right text-nowrap">
                  <Trans>Invoice Amount</Trans>
                </th>
                <th className="text-right text-nowrap">
                  <Trans>Payments</Trans>
                </th>
                <th className="text-right text-nowrap">
                  <Trans>Balance</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {i.data.map((j, idx) => (
                <tr key={idx}>
                  <td>{j.DateString}</td>
                  <td >
                    <DocumentLink document={j}>
                      {j.isInvoice
                        ? t("Invoice #{{ number }}", {
                            number: j.InvoiceNumber,
                          })
                        : null}
                      {j.isCreditNote
                        ? t("Credit Note #{{ number }}", {
                            number: j.CreditNoteNumber,
                          })
                        : null}
                    </DocumentLink>
                  </td>
                  <td>{j.Reference}</td>
                  <td>{j.DueDateString}</td>
                  <td className="text-right">
                    {formatAccountCurrency(j.Total)}
                  </td>
                  <td className="text-right">
                    {formatAccountCurrency(j.Payments)}
                  </td>
                  <td className="text-right">
                    {formatAccountCurrency(j.Remaining)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan="6" className="text-right">
                  <Trans>BALANCE DUE {{ currency: i.currency }}</Trans>
                </th>
                <th className="text-right">
                  {formatAccountCurrency(i.balanceDue)}
                </th>
              </tr>
            </tfoot>
          </Table>
        </div>
      ))}
      </div> : <div className="text-center mt-2"><p><Trans>No Data Available</Trans></p></div>}
      {grouped.length > 0 &&
      <div className="d-flex justify-content-end mt-3">
        <EmailDocument
          item={{
            Contact: contact,
            date: asAt,
            grouped,
          }}
          type="STATEMENT"
          className="mr-2"
        />
        <DownloadDocument className="mr-2" />
        <DocumentRenderer component={StatementDocument} />
      </div>}
    </Page>
  );
};

export const enhance = compose(
  withStateHandlers(
    {},
    {
      setState: () => (state) => ({ ...state }),
    }
  ),
  withDocumentRenderer({
    onDownload:
      ({ grouped, contact, date }) =>
      (getDocument) => {
        getDocument(
          {
            grouped,
            contact,
            date,
          },
          `statement_${contact.Name}.pdf`
        );
      },
  })
);

export default enhance(Statement);
